
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
  HiOutlineArrowUpRight,
  HiOutlineBookOpen,
} from "react-icons/hi2";

import { getAllBlogs } from "../redux/actions/blogs";
import { Loader, BlogsCard } from "../components/utils";
import ActionButton from "./layout/ActionButton";

/* ============================================
   BLOGS - HOME PAGE
============================================ */

function Blogs() {
  const dispatch = useDispatch();
  const { i18n, t } = useTranslation();

  const { isLoading, AllBlogs } = useSelector(
    (state) => state.Blogs || {}
  );

  const isEnglish = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "ar"
  )
    .toLowerCase()
    .startsWith("en");

  const content = {
    eyebrow: isEnglish
      ? "LEGAL INSIGHTS & KNOWLEDGE"
      : "المعرفة والثقافة القانونية",

    title: t("Blogs"),

    heading: isEnglish
      ? "Insights Beyond the Headlines"
      : "رؤى قانونية تستحق القراءة",

    description: isEnglish
      ? "Explore articles addressing legal questions, important principles, and practical aspects of the law."
      : "اكتشف مقالات تتناول المسائل القانونية والمبادئ المهمة والجوانب العملية للقانون بأسلوب واضح ومنظم.",

    allBlogs: isEnglish
      ? "Explore All Articles"
      : "استعرض جميع المقالات",

    empty: isEnglish
      ? "No articles are available at the moment."
      : "لا توجد مقالات متاحة حاليًا.",
  };

  /* ======================================
     FETCH BLOGS
  ====================================== */

  useEffect(() => {
    // Keep the existing API pagination convention
    dispatch(getAllBlogs(0, 6));
  }, [dispatch]);

  const blogs = Array.isArray(AllBlogs)
    ? AllBlogs.slice(0, 6)
    : [];

  // Editorial layout
  const featuredBlog = blogs[0];
  const sideBlogs = blogs.slice(1, 3);
  const remainingBlogs = blogs.slice(3, 6);

  return (
    <section
      id="latest-blogs"
      aria-labelledby="blogs-heading"
      className="
        relative isolate
        overflow-hidden

        border-t border-[#DCE6ED]
        bg-[#EDF3F7]

        py-16
        sm:py-20
        lg:py-24

        dark:border-white/10
        dark:bg-[#13293B]

        transition-colors duration-300
      "
    >
      {/* ======================================
          BACKGROUND DECORATION
      ====================================== */}

      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute -end-44 -top-44

          h-[480px] w-[480px]
          rounded-full

          bg-white/40
          blur-3xl

          dark:bg-[#294961]/15
        "
      />

      <div className="container relative z-10">

        {/* ======================================
            SECTION HEADING
        ====================================== */}

        <div
          className="
            mb-11 flex flex-col
            justify-between gap-8

            lg:mb-14
            lg:flex-row
            lg:items-end
          "
        >
          {/* Main heading */}
          <div className="max-w-[730px]">

            <div className="mb-5 flex items-center gap-3">

              <span
                aria-hidden="true"
                className="
                  h-px w-10
                  shrink-0
                  bg-skin-accent
                "
              />

              <span className="eyebrow text-skin-accent">
                {content.eyebrow}
              </span>

            </div>

            <h2
              id="blogs-heading"
              className="
                section-title
                text-skin-heading
              "
            >
              {content.heading}
            </h2>

            <p
              className="
                section-description
                mt-5 max-w-[670px]
                text-skin-muted
              "
            >
              {content.description}
            </p>

          </div>

          {/* Section identity */}
          <div
            className="
              hidden shrink-0
              items-center gap-4
              lg:flex
            "
          >
            <div className="h-px w-14 bg-skin-accent/50" />

            <div
              className="
                flex h-14 w-14
                items-center justify-center

                rounded-2xl
                border border-skin-border
                bg-white

                text-skin-accent

                dark:border-white/10
                dark:bg-[#1C354A]
              "
            >
              <HiOutlineBookOpen
                size={27}
                aria-hidden="true"
              />
            </div>
          </div>

        </div>

        {/* ======================================
            LOADING
        ====================================== */}

        {isLoading ? (
          <div
            className="
              flex min-h-[420px]
              items-center justify-center
            "
          >
            <Loader />
          </div>
        ) : blogs.length > 0 ? (
          <>
            {/* ==================================
                EDITORIAL FEATURED LAYOUT
            ================================== */}

            <div
              className="
                grid grid-cols-1
                gap-6

                lg:grid-cols-[1.45fr_1fr]
                lg:gap-7
              "
            >
              {/* Main featured article */}
              {featuredBlog && (
                <BlogsCard
                  blogs={featuredBlog}
                  variant="featured"
                  index={0}
                />
              )}

              {/* Two compact articles */}
              {sideBlogs.length > 0 && (
                <div
                  className="
                    grid min-w-0
                    grid-cols-1 gap-6

                    lg:grid-rows-2
                  "
                >
                  {sideBlogs.map((blog, index) => (
                    <BlogsCard
                      key={blog._id || blog.id || index}
                      blogs={blog}
                      variant="compact"
                      index={index + 1}
                    />
                  ))}
                </div>
              )}

            </div>

            {/* ==================================
                SECOND ROW
            ================================== */}

            {remainingBlogs.length > 0 && (
              <div
                className="
                  mt-6
                  grid grid-cols-1
                  gap-6

                  sm:grid-cols-2
                  lg:grid-cols-3
                  lg:gap-7
                "
              >
                {remainingBlogs.map((blog, index) => (
                  <BlogsCard
                    key={blog._id || blog.id || index}
                    blogs={blog}
                    variant="default"
                    index={index + 3}
                  />
                ))}
              </div>
            )}

            {/* ==================================
                VIEW ALL BLOGS
            ================================== */}

            <div
              className="
                mt-12 flex
                flex-col items-center
                gap-6
                lg:mt-14
              "
            >
              <div
                aria-hidden="true"
                className="
                  flex items-center gap-3
                "
              >
                <span className="h-px w-10 bg-skin-border" />
                <span className="h-1.5 w-1.5 rounded-full bg-skin-accent" />
                <span className="h-px w-10 bg-skin-border" />
              </div>

              <Link
                to="/BlogsCategory"
                onClick={() => window.scrollTo(0, 0)}
                className="
                  inline-flex rounded-xl

                  focus-visible:outline-none
                  focus-visible:ring-2
                  focus-visible:ring-skin-accent
                  focus-visible:ring-offset-4
                  focus-visible:ring-offset-skin-page
                "
              >
                <ActionButton
                  text={content.allBlogs}
                  icon={
                    <HiOutlineArrowUpRight
                
                    />
                  }
                  variant="primary"
                  className="min-h-[54px] px-7"
                />
              </Link>

            </div>
          </>
        ) : (
          /* ==================================
              EMPTY STATE
          ================================== */

          <div
            className="
              flex min-h-[300px]
              flex-col items-center
              justify-center gap-5

              rounded-2xl
              border border-skin-border
              bg-white
              p-8 text-center

              dark:border-white/10
              dark:bg-[#1C354A]
            "
          >
            <HiOutlineBookOpen
              size={38}
              aria-hidden="true"
              className="text-skin-accent"
            />

            <p className="body-2 text-skin-muted">
              {content.empty}
            </p>
          </div>
        )}

      </div>
    </section>
  );
}

export default Blogs;
